// @flow
import axios from 'lib/defaultClient';
import storage, { keys } from 'lib/storage';
import { TRACKING_EVENTS } from 'lib/trackingEvents';

const SESSION_KEY = '__tracking_session__';
const LAST_PAGE_KEY = '__tracking_last_page__';

type User = {
  id: string,
  [key: string]: any,
};

type TrackingSession = {
  sessionId: string,
  startedAt: string,
};

type PageInfo = {
  path: string,
  title: string,
  referrer: string,
};

type EventProperties = {
  productId?: string,
  productSlug?: string,
  productTitle?: string,
  productPriceId?: string,
  brand?: string,
  supplierUsername?: string,
  quantity?: number,
  [key: string]: any,
};

const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // RFC 4122 UUID v4 fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getSessionStorage = () => {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage;
};

const getSession = (): ?TrackingSession => {
  const ss = getSessionStorage();
  if (!ss) return null;
  try {
    const raw = ss.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const setSession = (session: TrackingSession): void => {
  const ss = getSessionStorage();
  if (!ss) return;
  ss.setItem(SESSION_KEY, JSON.stringify(session));
};

const getLastPage = (): ?string => {
  const ss = getSessionStorage();
  if (!ss) return null;
  return ss.getItem(LAST_PAGE_KEY);
};

const setLastPage = (path: string): void => {
  const ss = getSessionStorage();
  if (!ss) return;
  ss.setItem(LAST_PAGE_KEY, path);
};

const getUser = (): ?User => {
  return storage.get(keys.user);
};

const getPageInfo = (location?: { pathname: string, search: string }): PageInfo => {
  if (typeof window === 'undefined') {
    return { path: '', title: '', referrer: '' };
  }
  const path = location
    ? location.pathname + location.search
    : window.location.pathname + window.location.search;
  return {
    path,
    title: document.title || '',
    referrer: document.referrer || '',
  };
};

let clarityIdentified = false;

const setClarityTag = (key: string, value: string): void => {
  if (typeof window === 'undefined' || !window.clarity) return;
  try {
    window.clarity('set', key, value);
  } catch (e) {
    // ignore
  }
};

const setupClarityUserTags = (user: User): void => {
  if (user.grade && user.grade.name) {
    setClarityTag('grade', user.grade.name);
  }
  if (user.rule) {
    setClarityTag('rule', user.rule);
  }
};

const identifyClarity = (user: User): void => {
  if (clarityIdentified) return;
  if (typeof window === 'undefined' || !window.clarity) return;
  try {
    window.clarity('identify', user.id);
    setupClarityUserTags(user);
    clarityIdentified = true;
  } catch (e) {
    // ignore
  }
};

const pushToDataLayer = (page: PageInfo): void => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  window.dataLayer.push({
    event: 'pageview',
    page: {
      path: page.path,
      search: window.location.search || '',
      title: page.title,
    },
  });
};

let pendingSessionPromise: ?Promise<?string> = null;

const createSession = async (sessionId: string, startedAt: string): Promise<boolean> => {
  try {
    const page = getPageInfo();
    await axios.post('/tracking/sessions', {
      sessionId,
      startedAt,
      entryPath: page.path,
      referrer: page.referrer,
    });
    return true;
  } catch (e) {
    return false;
  }
};

const ensureSession = async (): Promise<?string> => {
  const user = getUser();
  if (!user) return null;

  identifyClarity(user);

  const session = getSession();
  if (session) return session.sessionId;

  if (pendingSessionPromise) return pendingSessionPromise;

  pendingSessionPromise = (async () => {
    const sessionId = generateUUID();
    const startedAt = new Date().toISOString();

    const success = await createSession(sessionId, startedAt);
    if (success) {
      setSession({ sessionId, startedAt });
      return sessionId;
    }
    return null;
  })();

  try {
    return await pendingSessionPromise;
  } finally {
    pendingSessionPromise = null;
  }
};

const sendEvent = async (
  sessionId: string,
  eventName: string,
  properties: EventProperties = {},
): Promise<void> => {
  try {
    const page = getPageInfo();
    await axios.post('/tracking/events', {
      eventId: generateUUID(),
      sessionId,
      eventName,
      occurredAt: new Date().toISOString(),
      page,
      properties,
    });
  } catch (e) {
    // fire-and-forget
  }
};

export const track = async (
  eventName: string,
  properties: EventProperties = {},
): Promise<void> => {
  const user = getUser();
  if (!user) return;

  const sessionId = await ensureSession();
  if (!sessionId) return;

  if (eventName === TRACKING_EVENTS.PRODUCT_VIEWED && properties.productSlug) {
    setClarityTag('viewedProduct', properties.productSlug);
  }
  if (eventName === TRACKING_EVENTS.PRODUCT_CART_ADDED) {
    setClarityTag('hasCartItems', 'true');
  }
  if (eventName === TRACKING_EVENTS.PRODUCT_WISHLIST_ADDED) {
    setClarityTag('hasWishlistItems', 'true');
  }
  if (eventName === TRACKING_EVENTS.CHAT_WIDGET_OPENED) {
    setClarityTag('openedChat', 'true');
  }

  await sendEvent(sessionId, eventName, properties);
};

export const trackPageView = async (location?: { pathname: string, search: string }): Promise<void> => {
  const page = getPageInfo(location);
  pushToDataLayer(page);
  await track(TRACKING_EVENTS.PAGE_VIEWED, {});
};

export const resetSession = (): void => {
  const ss = getSessionStorage();
  if (!ss) return;
  ss.removeItem(SESSION_KEY);
  ss.removeItem(LAST_PAGE_KEY);
  pendingSessionPromise = null;
  clarityIdentified = false;
};

export const setTag = (key: string, value: string): void => {
  setClarityTag(key, value);
};



// WEBPACK FOOTER //
// ./src/lib/tracking.js